const makeSecureImageUrl = require("./makeSecureImageUrl")

/**
 * This function takes a collection of images, a transform, a width, a height,
 * and a boolean whether to fallback to the "original" image if we don't find
 * one large enough and returns a transformed image with a new path to a secure image
 * @param {Object} images - collection of images, a single image, or image object with crops
 * @param {String} transform to apply to image
 * @param {number} width - min width of the ideal image
 * @param {number} height - min height of the ideal image
 * @param {boolean} noFallback - whether to fall back to the original if no ideal crop is found
 * @param {String} seoImageName - the text name to apply to the file name
 * @returns {String} ideal image with transformation or null if no images passed in
 */
const getIdealImage = (images, transform, width, height, noFallback = false, seoImageName) => {
  if (images === null || typeof images === "undefined") {
    return null
  }

  const aspectRatio = width / height
  const { crops } = images

  let contender = null
  let fallback
  // new image data format with crops
  if (crops && crops.length) {
    // add the original image to the crops as a default
    if (images.width && images.height) {
      const crop = {
        path: images.path,
      }
      if (crops[0].cropStart) {
        // new style with crop start and end
        crop.cropStart = {
          x: 0,
          y: 0,
        }
        crop.cropEnd = {
          x: images.width,
          y: images.height,
        }
      } else {
        // older style with width and height
        crop.width = images.width
        crop.height = images.height
      }
      crops.push(crop)
    }
    fallback = images.path
    crops.forEach((crop) => {
      const image = {}
      if (crop.cropStart && crop.cropEnd) {
        // make a custom crop from the original image
        image.width = crop.cropEnd.x - crop.cropStart.x
        image.height = crop.cropEnd.y - crop.cropStart.y

        const cropTransform = `c_crop,x_${crop.cropStart.x},y_${crop.cropStart.y},w_${image.width},h_${image.height}`
        image.fileName = makeSecureImageUrl(
          images.path,
          `${cropTransform}/${transform}`,
          seoImageName,
        )
      } else if (crop.path && crop.width && crop.height) {
        // it's an image with path, width and height
        image.width = crop.width
        image.height = crop.height
        image.fileName = makeSecureImageUrl(crop.path, transform, seoImageName)
      } else if (crop.width && crop.height) {
        // it's an image with a width and height
        image.width = crop.width
        image.height = crop.height
        image.fileName = makeSecureImageUrl(images.path, transform, seoImageName)
      }

      if (image.width > 0 && image.height > 0) {
        image.aspectRatio = image.width / image.height
        if (image.width >= width && image.height >= height) {
          // equal to or larger than what we need
          if (contender === null) {
            contender = {
              fileName: image.fileName,
              aspectRatio: image.aspectRatio,
            }
          } else if (
            Math.abs(aspectRatio - image.aspectRatio) <
            Math.abs(aspectRatio - contender.aspectRatio)
          ) {
            // new image a better match for aspect ratio
            contender = {
              fileName: image.fileName,
              aspectRatio: image.aspectRatio,
            }
          }
        }
      }
    })
    if (contender !== null) {
      return contender.fileName
    }

    if (noFallback) return null
    return makeSecureImageUrl(fallback, transform, seoImageName)
  }

  if (!crops && !images.path) {
    // legacy images collection data format
    images.forEach(({ path, fileName, dimensions }) => {
      const image = {
        fileName: path || fileName,
        dimensions,
      }

      image.fileName = makeSecureImageUrl(image.fileName, transform, seoImageName)

      if (image.dimensions) {
        const dims = image.dimensions.split(/x/)

        if (dims.length === 2) {
          // dimensions have width and height
          image.width = Number(dims[0])
          image.height = Number(dims[1])
          image.aspectRatio = image.width / image.height

          if (image.width >= width && image.height >= height) {
            // equal to or larger than what we need
            if (contender === null) {
              contender = {
                fileName: image.fileName,
                aspectRatio: image.aspectRatio,
              }
            } else if (
              Math.abs(aspectRatio - image.aspectRatio) <
              Math.abs(aspectRatio - contender.aspectRatio)
            ) {
              // new image a better match for aspect ratio
              contender = {
                fileName: image.fileName,
                aspectRatio: image.aspectRatio,
              }
            }
          }
        } else {
          // set fallback image to original
          fallback = image.fileName
        }
      } else {
        fallback = image.fileName
      }
    })

    if (contender !== null) {
      return contender.fileName
    }

    if (noFallback) return null
    return fallback
  }

  if (!noFallback && images.path) {
    return makeSecureImageUrl(images.path, transform, seoImageName)
  }
  return null
}

module.exports = getIdealImage
